import { useCurrentUser } from "hooks/auth";

import type { EvaluationPanelMember } from "../../types/panel.type";

interface EvaluationInviteEmailContentProps {
	additionalMessage?: string;
	companyName: string;
	deadline: string;
	pendingMembers?: EvaluationPanelMember[];
	rfxTitle: string;
	senderName?: string;
}

const getMemberDisplayName = (member: EvaluationPanelMember): string => {
	if (member.name) {
		return member.name;
	}

	if (member.user?.full_name) {
		return member.user.full_name;
	}

	return member.email;
};

/**
 * A guest invitee is an external member with no linked registered user account
 * (mirrors the real Blade's `is_guest` gate from PRO-596/Task 005, where a guest
 * has a GuestInvite row and `user_id = null`). Guests receive the free-account
 * notice in the actual email, so the preview must show it too.
 */
const isGuestMember = (member: EvaluationPanelMember): boolean =>
	member.member_type === "external" && !member.user_id && !member.user;

export const EvaluationInviteEmailContent = ({
	companyName,
	rfxTitle,
	deadline,
	additionalMessage,
	pendingMembers = [],
}: EvaluationInviteEmailContentProps) => {
	const { currentUser } = useCurrentUser();
	const hasGuestInvitee = pendingMembers.some(isGuestMember);
	const tdStyle: React.CSSProperties = {
		fontFamily: "Arial, sans-serif",
		fontSize: "16px",
		lineHeight: "24px",
		color: "#374151",
		paddingBottom: "20px",
	};

	return (
		<table
			width="100%"
			style={{ backgroundColor: "#ffffff" }}
		>
			<tbody>
				<tr>
					<td align="center">
						{/*
						 * Fixed HTML `width="600"` (a pixel value, not a percentage) fights the
						 * `maxWidth: "600px"` style: browsers use the HTML `width` attribute as a
						 * hard minimum for table auto-layout, so this table refused to shrink
						 * below 600px even when its centered `<td>` (inside the drawer's
						 * `col-span-2` grid column) was narrower than that. The forced-wide,
						 * horizontally-centered table then overflowed symmetrically past its own
						 * box (default `overflow: visible`) — bleeding left across the grid
						 * column boundary into `col-span-1` where the Send button lives. Because
						 * the overflow is mostly unpainted table whitespace, it's invisible while
						 * still intercepting clicks. Using `width="100%"` lets the table shrink to
						 * fit its container, while `maxWidth: "600px"` still caps it on wide
						 * screens.
						 */}
						<table
							width="100%"
							style={{ maxWidth: "600px" }}
						>
							<tbody>
								<tr>
									<td
										style={{
											...tdStyle,
											paddingTop: "40px",
										}}
									>
										{pendingMembers.length > 0 ? (
											<>
												This invitation will be sent to:
												<div style={{ marginTop: "12px" }}>
													{pendingMembers.map((member) => (
														<div
															key={member.id}
															style={{ marginBottom: "8px" }}
														>
															<strong>{getMemberDisplayName(member)}</strong>
															<br />
															<span style={{ color: "#6B7280" }}>
																{member.email}
															</span>
														</div>
													))}
												</div>
											</>
										) : (
											"Hi [Panel Member Name],"
										)}
									</td>
								</tr>
								<tr>
									<td style={tdStyle}>
										You have been invited to join the evaluation panel for{" "}
										<strong>{rfxTitle || "[RFx Title]"}</strong> by{" "}{currentUser.full_name && <><strong>{currentUser.full_name}</strong> from{" "}</>}
										<strong>{companyName || "[Company Name]"}</strong>.
									</td>
								</tr>
								<tr>
									<td style={tdStyle}>
										As a panel member, you will be asked to review and score
										supplier submissions against the defined evaluation criteria.
									</td>
								</tr>
								<tr>
									<td style={tdStyle}>
										<strong>Evaluation deadline:</strong> {deadline || "[Deadline]"}
									</td>
								</tr>
								{additionalMessage && (
									<tr>
										<td
											style={{
												...tdStyle,
												backgroundColor: "#F9FAFB",
												padding: "12px 16px",
												borderLeft: "4px solid #3B82F6",
												borderRadius: "4px",
											}}
										>
											<span
												style={{
													fontSize: "14px",
													fontWeight: 600,
													color: "#6B7280",
													display: "block",
													marginBottom: "4px",
												}}
											>
												Message from the inviter:
											</span>
											{additionalMessage}
										</td>
									</tr>
								)}
								{hasGuestInvitee && (
									<tr>
										<td
											style={tdStyle}
											data-testid="guest-free-account-notice"
										>
											As your organisation is not registered with Procuracon,
											you will be asked to create a free account before
											continuing.
										</td>
									</tr>
								)}
								<tr>
									<td style={tdStyle}>
										<span
											style={{
												display: "inline-block",
												backgroundColor: "#2563EB",
												color: "#ffffff",
												padding: "12px 24px",
												borderRadius: "6px",
												textDecoration: "none",
												fontWeight: 600,
												fontSize: "14px",
												cursor: "default",
											}}
										>
											View Evaluation in Procuracon
										</span>
									</td>
								</tr>
								<tr>
									<td style={tdStyle}>
										Should you have any questions, feel free to contact the
										evaluation lead directly.
									</td>
								</tr>
								<tr>
									<td
										style={{
											fontFamily: "Arial, sans-serif",
											fontSize: "16px",
											lineHeight: "24px",
											color: "#374151",
										}}
									>
										<br />
										Best Regards,
										<br />
										<br />
										<b>The Procuracon Team</b>
										<br />
										<i>Supporting transparent and efficient procurement</i>
									</td>
								</tr>
							</tbody>
						</table>
					</td>
				</tr>
			</tbody>
		</table>
	);
};
